import { IPLink, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { TFunction } from 'i18next';
import InternalAudit, { AuditStatus } from 'models/internalAudit';
import { InternalAuditSchedule, InternalAuditScheduleOccurrence } from 'models/internalAuditTemplate';
import { useTranslation } from 'react-i18next';
import { toLocaleDateShort } from 'utils/datetime';

interface IAuditPeriodCalloutProps {
  schedule: InternalAuditSchedule;
  occurrence?: InternalAuditScheduleOccurrence;
  calculatedDate?: Date;
  onOpenAudit?: (occurrence: InternalAuditScheduleOccurrence) => void;
}

// Hover card of one chip in the audit row of the annual plan: an actual occurrence (openable)
// or a calculated future occurrence (informational only).
export const AuditPeriodCallout = (props: IAuditPeriodCalloutProps) => {
  // 'control' carries the recurring pattern summary wording (RecurringPattern.getSummary)
  const { t } = useTranslation(['audit', 'translation', 'control']);

  const pattern = props.schedule.template.recurrencePattern;

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { maxWidth: 400, padding: 20 } }}>
      <IPStack.Item>
        <IPText styles={globalTextStylesBold}>{t('audit:AnnualPlan.RowTag')}</IPText>
      </IPStack.Item>
      {props.occurrence && (
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              {props.onOpenAudit ? (
                <IPLink underline onClick={() => props.onOpenAudit?.(props.occurrence as InternalAuditScheduleOccurrence)}>
                  {props.occurrence.name}
                </IPLink>
              ) : (
                <IPText>{props.occurrence.name}</IPText>
              )}
            </IPStack.Item>
            <IPStack.Item>
              <IPText>
                {t('audit:List.Columns.Status')}:{' '}
                {InternalAudit.getStatusText(props.occurrence.status as AuditStatus, t as TFunction<string[]>)}
              </IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPText>{t('audit:AnnualPlan.ScopeCount', { count: props.occurrence.scopeItemCount })}</IPText>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
      {props.calculatedDate && (
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            {/* a calculated occurrence has no name of its own, so name the series it belongs to */}
            <IPStack.Item>
              <IPText>{props.schedule.template.name}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPText>{t('audit:AnnualPlan.PlannedOn', { date: toLocaleDateShort(props.calculatedDate) })}</IPText>
            </IPStack.Item>
            {pattern.isActive && (
              <IPStack.Item>
                <IPText variant="small">{pattern.getSummary()}</IPText>
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPText variant="small">{t('audit:AnnualPlan.CalculatedInfo')}</IPText>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
